import { Button, Card, Group, NativeSelect, Stack, Text } from "@mantine/core";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/useAuth";
import { CardRefundStatus } from "@/components/CardRefundStatus";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState, ErrorState } from "@/components/states";
import { cardRefundConfig } from "@/entities/configs/cardRefunds";
import { COLLECTIONS } from "@/firestore/collections";
import { usePaginatedQuery } from "@/firestore/hooks";
import type { CardRefundDoc } from "@/firestore/cardRefunds";
import { entityPath } from "@/routes/paths";
import { refundQuery, type RefundFilter } from "./card-refunds/presentation";

const FILTERS: { value: RefundFilter; label: string }[] = [
  { value: "toPay", label: "To pay" },
  { value: "mine", label: "Mine" },
  { value: "inProgress", label: "In progress" },
  { value: "done", label: "Done" },
  { value: "needsReview", label: "Needs review" },
  { value: "all", label: "All" },
];

export function CardRefundQueuePage() {
  const { claims } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const filter = FILTERS.find((f) => f.value === params.get("view"))?.value ?? "toPay";
  const operatorId = claims?.backendId ?? "";
  const query = usePaginatedQuery<CardRefundDoc>(
    COLLECTIONS.cardRefunds,
    refundQuery(filter, operatorId),
    50,
    filter !== "mine" || !!operatorId,
  );

  return (
    <>
      <PageHeader
        title="Card refunds"
        subtitle="Claim a settled refund, transfer USDC to the user's Main, then verify the transfer."
      />
      <Card withBorder padding="lg">
        <Stack>
          <Group justify="space-between" align="end">
            <NativeSelect
              label="Refund queue"
              value={filter}
              data={FILTERS}
              onChange={(e) => setParams({ view: e.currentTarget.value })}
            />
            <Button variant="default" size="xs" onClick={query.refresh}>
              Refresh
            </Button>
          </Group>
          <Text size="sm" c="dimmed">
            Oldest received first. Mine includes your completed cases. New cases and assignments
            update live.
          </Text>
          {query.error ? (
            <ErrorState error={query.error} />
          ) : (
            <DataTable
              data={query.data}
              columns={cardRefundConfig.columns}
              loading={query.loading}
              hasMore={query.hasMore}
              onLoadMore={query.loadMore}
              onRowClick={(row) => navigate(entityPath("cardRefund", row.id))}
              searchPlaceholder="Filter loaded refunds…"
              emptyState={
                <EmptyState
                  title="No refunds in this queue"
                  description="Change the queue to inspect other states."
                />
              }
            />
          )}
          <Group gap="xs">
            <CardRefundStatus state="done" />
            <Text size="xs" c="dimmed">
              Done means the backend verified the user's received USDC.
            </Text>
          </Group>
        </Stack>
      </Card>
    </>
  );
}
